import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { xor } from 'lodash-es';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { DashpivotEvent, EventNotifierService, EventTypes } from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { IBusinessAttribute } from 'app/business-attributes/business-attribute.model';
import { SegmentService } from 'app/segment/segment.service';
import { TemplateWeb } from 'app/shared/model/template.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';

@Component({
  selector: 'cc-sign-up-templates-modal.component-modal',
  templateUrl: 'sign-up-templates-modal.component.html',
  styleUrls: [
    'sign-up-templates-modal.component.scss',
    '../templates-selector/templates-selector-common.scss',
  ],
})
export class SignUpTemplatesModalForComponent implements OnInit {
  @Input() businessAttributes: IBusinessAttribute[] = [];

  templates: TemplateWeb[] = [];
  teamId: string;
  enableAddTemplateBtn = false;
  loading = true;
  isSingleSelectionOnly: boolean;
  filtersInFlight: string[];
  libraryRequestInFlight;
  selectedBusinessAttributes: string[] = [];

  private destroy$ = new Subject<void>();
  readonly signUpContext = 'SignUp';

  constructor(
    public readonly modal: NgbActiveModal,
    private readonly segmentService: SegmentService,
    private readonly templatesService: TemplatesService,
    private readonly errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    this.selectedBusinessAttributes = this.businessAttributes.map((attr) => attr._id);
  }

  onFacetsChange(businessAttributes: IBusinessAttribute[]) {
    const selectedAttributeIds = businessAttributes.map((attr) => attr._id);

    // If the user has selected the same filters as the ones in flight, do nothing
    if (this.loading && this.filtersInFlight && !xor(selectedAttributeIds, this.filtersInFlight).length) {
      return;
    }

    this.filtersInFlight = selectedAttributeIds;
    this.loading = true;

    // cancel the previous request if still active and start a new one
    this.libraryRequestInFlight?.unsubscribe();
    this.libraryRequestInFlight = this.templatesService
      .getLibraryTemplates(businessAttributes)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          delete this.libraryRequestInFlight;
        }),
      )
      .subscribe(
        (templates) => {
          this.templates = templates;
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addTemplates() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.TemplateSignUpSelected),
      this.segmentService,
    );

    const selectedTemplates = this.templates.filter((template) => template.selected);
    const selectedIds = selectedTemplates.map(({ id }) => id);

    this.templatesService
      .copyFromLibraryTemplateToTeam(selectedIds, this.teamId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (copiedTemplates) => {
          const copiedTemplateIds = copiedTemplates.map((template) => template._id);
          this.trackTemplateAddedEvent(selectedTemplates);
          this.modal.close(copiedTemplateIds);
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  private trackTemplateAddedEvent(templates: TemplateWeb[]) {
    templates.forEach(({ uniqueAppId }) => {
      void EventNotifierService.notify(
        new DashpivotEvent(EventTypes.TemplatePublicAdded, {
          Context: this.signUpContext,
          TemplateID: uniqueAppId,
        }),
        this.segmentService,
      );
    });
  }

  updateAddButtonStatus(enable: boolean) {
    this.enableAddTemplateBtn = enable;
  }

  skip() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.TemplateSignUpSelectionSkipped),
      this.segmentService,
    );
    this.modal.close();
  }
}
